import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';

import { useBoolean } from 'src/hooks/use-boolean';

import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { Comment, ThreadType, NotesFeedSource } from 'src/services/threads/threads.types';
import { useGetThreadQuery, useGetNotesFeedQuery, useDeleteCommentMutation, useLazyGetCommentsThreadQuery } from 'src/services/threads/threads.service';

import ListSkeleton from 'src/components/list/list-skeleton';
import { ConfirmDialog } from 'src/components/custom-dialog';

import CommentEditor from './comment-editor';
import PostCommentItem from './post-comment-item';

// ----------------------------------------------------------------------

type Props = {
  /** Single-thread mode: the notes on exactly this thread. */
  thread_id?: string;
  /**
   * Merged mode: the notes on a record and everything merged into it — a candidate's own notes plus
   * those written on their applications, or the other way round. Takes precedence over `thread_id`.
   */
  feed?: NotesFeedSource;
  comments_per_page?: number;
};

const DEFAULT_PAGE = {
  page: 1,
  per_page: 5,
};

export default function PostCommentList({ thread_id, feed, comments_per_page = DEFAULT_PAGE.per_page }: Props) {

  const commentEditorControl = useBoolean();
  const commentDeleteControl = useBoolean();

  const dispatch = useAppDispatch();
  const [currentComment, setCurrentComment] = useState<Comment | null>(null);

  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const [page, setPage] = useState<PageOptions>({
    page: DEFAULT_PAGE.page,
    per_page: comments_per_page,
  });

  const { currentData: thread, isLoading: isLoadingThread } = useGetThreadQuery({
    thread_id: thread_id as string
  }, {
    skip: !thread_id || !!feed
  });

  const [getComments, { currentData: threadComments }] = useLazyGetCommentsThreadQuery();

  const { currentData: feedComments, isLoading: isLoadingFeed } = useGetNotesFeedQuery({
    ...(feed as NotesFeedSource),
    ...page,
  }, {
    skip: !feed
  });

  useEffect(() => {
    if (!thread_id || feed) return;

    getComments({
      thread_id,
      ...page,
    });
  }, [getComments, page, thread_id, feed]);

  const currentData = feed ? feedComments : threadComments;

  const isLoading = feed ? isLoadingFeed : isLoadingThread;

  // A merged feed spans several application threads and narrows when the filter changes; holding the
  // old page would land the reader past the end of the narrowed set. Returning `prev` untouched when
  // already on page 1 lets React bail out rather than re-rendering every list on mount.
  useEffect(() => {
    if (!feed) return;

    setPage((prev) => (prev.page === DEFAULT_PAGE.page ? prev : { ...prev, page: DEFAULT_PAGE.page }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed?.type, feed?.id, feed?.application_id]);

  const onChangePage = (_event: any, newPage: number) => {
    setPage((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const openEditor = (comment: Comment) => {
    setCurrentComment(comment);
    commentEditorControl.onTrue();
  }

  const closeEditor = () => {
    setCurrentComment(null);
    commentEditorControl.onFalse();
  }

  const onCommentEdit = () => {
    // Refresh comments. In merged mode the edited note may belong to any thread in the set, so the
    // feed tag is what has to go — invalidating this list's own thread would refresh nothing.
    dispatch(rootApi.util.invalidateTags(
      feed ? ['NotesFeed'] : [{ type: 'Comments', id: thread_id as string }]
    ));
    closeEditor();
  }

  const openDeleteConfirm = (comment: Comment) => {
    setCurrentComment(comment);
    commentDeleteControl.onTrue();
  }

  const closeDeleteConfirm = useCallback(() => {
    setCurrentComment(null);
    commentDeleteControl.onFalse();
  }, [commentDeleteControl]);

  const handleCommentDelete = useCallback(async () => {
    if (!currentComment) return;

    try {
      await deleteComment({
        // The comment's own thread — in a merged feed it is not necessarily this list's.
        thread_id: currentComment.thread_id ?? (thread_id as string),
        comment_id: currentComment.id
      }).unwrap();

      closeDeleteConfirm();
      enqueueSnackbar(t('threads.api.delete_comment.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('threads.api.delete_comment.default_error'), { variant: 'error' });
    }
  }, [currentComment, deleteComment, thread_id, closeDeleteConfirm]);

  return (
    <Box id="comment-list">

      {
        isLoading && (
          <Stack flexDirection="column">
            <ListSkeleton height={40} />
          </Stack>
        )
      }

      {
        !isLoading && (
          <>
            {currentData?.results.map((comment) => (
              <Box key={comment.id}>
                <PostCommentItem
                  comment={comment}
                  // The badge only earns its space where notes from more than one record are mixed
                  // together; on a single thread the reader already knows where they are.
                  showSource={!!feed}
                  onEdit={() => openEditor(comment)}
                  onDelete={() => openDeleteConfirm(comment)}
                />
              </Box>
            ))}
          </>
        )
      }

      {!!currentData?.count &&
        <Stack flexDirection="row" justifyContent="center">
          <Pagination
            onChange={onChangePage}
            page={page.page}
            count={
              currentData?.count && currentData.count > page.per_page
                ? Math.ceil(currentData.count / page.per_page)
                : 1
            }
            sx={{ mt: 1, mx: 'auto' }}
          />
        </Stack>
      }

      {!currentData?.count &&
        <Stack flexDirection="row" justifyContent="center" sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {/* A merged feed is always notes; there is no single thread to take the type from. */}
            {t(`threads.comments.empty_${feed ? ThreadType.NOTES : (thread?.type ?? 'chat')}`)}
          </Typography>
        </Stack>
      }

      {
        (commentEditorControl.value && currentComment) && (
          <CommentEditor
            comment={currentComment as Comment}
            open={commentEditorControl.value}
            onClose={closeEditor}
            onSuccess={onCommentEdit}
          />
        )
      }

      <ConfirmDialog
        open={commentDeleteControl.value}
        onClose={commentDeleteControl.onFalse}
        title={t('threads.comments.delete_comment')}
        content={t('threads.comments.delete_confirm')}
        action={
          <LoadingButton variant="contained" color="error" onClick={handleCommentDelete} loading={isDeleting}>
            {t('common.delete')}
          </LoadingButton>
        }
      />
    </Box>
  );
}
